import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Clock, Briefcase, Send, CheckCircle, ChevronDown, ChevronUp, Upload, FileText, Share2, Copy, X } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const typeColors = {
  "full-time": "text-[#32D74B]",
  "part-time": "text-[#007AFF]",
  "contract": "text-[#FF9F0A]",
  "freelance": "text-[#BF5AF2]",
};

// ── File Upload Component ──
const FileUploadField = ({ onUpload, uploadedFile }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Max 10MB.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/upload`, formData);
      onUpload({ url: `${BASE_URL}${data.url}`, filename: data.filename });
      toast.success("File uploaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="mono-font text-xs text-[#52525B] mb-1.5 block uppercase tracking-wider">
        Resume / CV
      </label>
      {uploadedFile ? (
        <div className="flex items-center gap-3 glass-input">
          <FileText className="w-4 h-4 text-[#17B352]" />
          <span className="text-sm text-[#A1A1AA] flex-1 truncate">{uploadedFile.filename}</span>
          <button type="button" onClick={() => onUpload(null)} className="text-[#52525B] hover:text-red-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileRef.current?.click()}
          className="glass-input flex items-center gap-3 cursor-pointer hover:border-[#17B352] transition-colors"
        >
          <Upload className="w-4 h-4 text-[#52525B]" />
          <span className="text-[#52525B] text-sm">
            {uploading ? "Uploading..." : "Click to upload PDF, DOC, DOCX (max 10MB)"}
          </span>
        </div>
      )}
      <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.rtf" onChange={handleFile} />
    </div>
  );
};

// ── Application Form ──
const ApplicationForm = ({ jobSlug, onSuccess }) => {
  const [formData, setFormData] = useState({ name: "", email: "", cover_letter: "" });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleApply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/jobs/${jobSlug}/apply`, {
        ...formData,
        job_id: jobSlug,
        resume_url: uploadedFile?.url || null,
        resume_filename: uploadedFile?.filename || null,
      });
      onSuccess();
      toast.success("Application submitted!");
    } catch {
      toast.error("Failed to submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleApply} className="space-y-4 pt-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="mono-font text-xs text-[#52525B] mb-1.5 block uppercase tracking-wider">Name</label>
          <input data-testid="job-apply-name" type="text" required className="glass-input" placeholder="Your name"
            value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
        </div>
        <div>
          <label className="mono-font text-xs text-[#52525B] mb-1.5 block uppercase tracking-wider">Email</label>
          <input data-testid="job-apply-email" type="email" required className="glass-input" placeholder="your@email.com"
            value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
        </div>
      </div>
      <FileUploadField uploadedFile={uploadedFile} onUpload={setUploadedFile} />
      <div>
        <label className="mono-font text-xs text-[#52525B] mb-1.5 block uppercase tracking-wider">Cover Letter</label>
        <textarea data-testid="job-apply-cover" className="glass-textarea" placeholder="Why are you a great fit?"
          value={formData.cover_letter} onChange={(e) => setFormData({ ...formData, cover_letter: e.target.value })} />
      </div>
      <div className="flex gap-3">
        <button data-testid="job-apply-submit" type="submit" disabled={submitting}
          className="glow-button flex items-center gap-2 disabled:opacity-50">
          {submitting ? "Submitting..." : <><Send className="w-4 h-4" /> Submit Application</>}
        </button>
      </div>
    </form>
  );
};

// ── Share Button ──
const ShareButton = ({ slug }) => {
  const copyLink = () => {
    const url = `${window.location.origin}/careers/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };
  return (
    <button onClick={(e) => { e.stopPropagation(); copyLink(); }}
      className="p-2 rounded-lg hover:bg-[rgba(255,255,255,0.05)] text-[#52525B] hover:text-[#17B352] transition-colors"
      title="Copy job link">
      <Share2 className="w-4 h-4" />
    </button>
  );
};

// ── Job Card ──
const JobCard = ({ job, isExpanded, onToggle }) => {
  const [showApply, setShowApply] = useState(false);
  const [applied, setApplied] = useState(false);

  return (
    <motion.div data-testid={`job-card-${job.id}`} className="glass-card rounded-xl overflow-hidden"
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <div className="p-6 cursor-pointer" onClick={onToggle}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-xl md:text-2xl mb-2">{job.title}</h3>
            <div className="flex flex-wrap gap-4 mono-font text-xs text-[#A1A1AA]">
              <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" />{job.department}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{job.location}</span>
              <span className={`flex items-center gap-1.5 ${typeColors[job.type] || "text-[#A1A1AA]"}`}>
                <Clock className="w-3.5 h-3.5" />{job.type}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ShareButton slug={job.slug || job.id} />
            {isExpanded ? <ChevronUp className="w-5 h-5 text-[#52525B]" /> : <ChevronDown className="w-5 h-5 text-[#52525B]" />}
          </div>
        </div>
      </div>

      {isExpanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
          className="px-6 pb-6 border-t border-[rgba(255,255,255,0.05)]">
          <div className="pt-5 space-y-5">
            <div>
              <h4 className="mono-font text-xs text-[#52525B] uppercase tracking-wider mb-2">Description</h4>
              <p className="text-[#A1A1AA] leading-relaxed whitespace-pre-line">{job.description}</p>
            </div>
            <div>
              <h4 className="mono-font text-xs text-[#52525B] uppercase tracking-wider mb-2">Requirements</h4>
              <div className="flex flex-wrap gap-2">
                {job.requirements.split(/[,\n]+/).map((req) => req.trim()).filter(Boolean).map((req, i) => (
                  <span key={i} className="inline-flex items-center mono-font text-xs px-3 py-1.5 rounded-full border border-[rgba(255,255,255,0.08)] text-[#A1A1AA] bg-[rgba(255,255,255,0.03)]">
                    {req}
                  </span>
                ))}
              </div>
            </div>

            {!showApply && !applied && (
              <button data-testid={`apply-btn-${job.id}`} onClick={(e) => { e.stopPropagation(); setShowApply(true); }}
                className="glow-button flex items-center gap-2">
                <Send className="w-4 h-4" /> Apply Now
              </button>
            )}

            {applied && (
              <div className="flex items-center gap-3 text-[#32D74B]">
                <CheckCircle className="w-5 h-5" />
                <span>Application submitted! We'll be in touch.</span>
              </div>
            )}

            {showApply && !applied && (
              <ApplicationForm jobSlug={job.slug || job.id} onSuccess={() => setApplied(true)} />
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// ── Job Detail Page (public shareable link) ──
const JobDetailPage = () => {
  const { jobSlug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/jobs/${jobSlug}`)
      .then((r) => setJob(r.data))
      .catch(() => setJob(null))
      .finally(() => setLoading(false));
  }, [jobSlug]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied!");
  };

  if (loading) return <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center mono-font text-[#52525B]">Loading...</div>;
  if (!job) return (
    <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-3xl mb-4 italic">Job not found</h2>
        <Link to="/careers" className="glow-button inline-block">View All Positions</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#E5E5E5]">
      <nav className="fixed top-0 left-0 right-0 z-50 nav-scrolled">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center"><img src="/graphikpro-logo.png" alt="Graphikpro" className="h-8 w-auto" /></Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="mono-font text-xs tracking-[0.2em] text-[#A1A1AA] hover:text-[#17B352] transition-colors uppercase">Home</Link>
            <Link to="/careers" className="mono-font text-xs tracking-[0.2em] text-[#17B352] uppercase">Careers</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button onClick={() => navigate("/careers")}
            className="flex items-center gap-2 mono-font text-xs text-[#52525B] hover:text-[#17B352] transition-colors uppercase tracking-wider mb-8">
            <ArrowLeft className="w-4 h-4" /> All Positions
          </button>

          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl italic mb-4">{job.title}</h1>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 mono-font text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#17B352]/30 text-[#17B352] bg-[rgba(23,179,82,0.08)]">
                  <Briefcase className="w-3 h-3" />{job.department}
                </span>
                <span className="inline-flex items-center gap-1.5 mono-font text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#007AFF]/30 text-[#007AFF] bg-[rgba(0,122,255,0.08)]">
                  <MapPin className="w-3 h-3" />{job.location}
                </span>
                <span className={`inline-flex items-center gap-1.5 mono-font text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border ${
                  job.type === 'full-time' ? 'border-[#32D74B]/30 text-[#32D74B] bg-[rgba(50,215,75,0.08)]' :
                  job.type === 'contract' ? 'border-[#FF9F0A]/30 text-[#FF9F0A] bg-[rgba(255,159,10,0.08)]' :
                  job.type === 'freelance' ? 'border-[#BF5AF2]/30 text-[#BF5AF2] bg-[rgba(191,90,242,0.08)]' :
                  'border-[#007AFF]/30 text-[#007AFF] bg-[rgba(0,122,255,0.08)]'
                }`}>
                  <Clock className="w-3 h-3" />{job.type}
                </span>
              </div>
            </div>
            <button onClick={copyLink} className="secondary-button text-sm py-2 px-4 flex items-center gap-2">
              <Copy className="w-4 h-4" /> Share
            </button>
          </div>

          <div className="glass-card rounded-xl p-6 md:p-8 space-y-8 mb-8">
            <div>
              <h3 className="mono-font text-xs text-[#52525B] uppercase tracking-wider mb-3">Description</h3>
              <p className="text-[#A1A1AA] leading-relaxed whitespace-pre-line">{job.description}</p>
            </div>
            <div>
              <h3 className="mono-font text-xs text-[#52525B] uppercase tracking-wider mb-3">Requirements</h3>
              <div className="flex flex-wrap gap-2">
                {job.requirements.split(/[,\n]+/).map((req, i) => req.trim()).filter(Boolean).map((req, i) => (
                  <span key={i} className="inline-flex items-center mono-font text-xs px-3 py-1.5 rounded-full border border-[rgba(255,255,255,0.08)] text-[#A1A1AA] bg-[rgba(255,255,255,0.03)]">
                    {req}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {!showApply && !applied && (
            <button data-testid="job-detail-apply-btn" onClick={() => setShowApply(true)}
              className="glow-button flex items-center gap-2">
              <Send className="w-4 h-4" /> Apply for this Position
            </button>
          )}

          {applied && (
            <div className="glass-card rounded-xl p-6 flex items-center gap-3 text-[#32D74B]">
              <CheckCircle className="w-5 h-5" />
              <span>Application submitted! We'll be in touch.</span>
            </div>
          )}

          {showApply && !applied && (
            <div className="glass-card rounded-xl p-6 md:p-8">
              <h3 className="text-xl mb-4">Apply for {job.title}</h3>
              <ApplicationForm jobSlug={job.slug || job.id} onSuccess={() => setApplied(true)} />
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

// ── Careers Listing Page ──
const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filterDept, setFilterDept] = useState("all");

  useEffect(() => {
    axios.get(`${API}/jobs`).then((r) => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const departments = ["all", ...new Set(jobs.map((j) => j.department))];
  const filtered = filterDept === "all" ? jobs : jobs.filter((j) => j.department === filterDept);

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#E5E5E5]">
      <nav className="fixed top-0 left-0 right-0 z-50 nav-scrolled">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center"><img src="/graphikpro-logo.png" alt="Graphikpro" className="h-8 w-auto" /></Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="mono-font text-xs tracking-[0.2em] text-[#A1A1AA] hover:text-[#17B352] transition-colors uppercase">Home</Link>
            <Link to="/careers" className="mono-font text-xs tracking-[0.2em] text-[#17B352] uppercase">Careers</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 pt-28 pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <p className="mono-font text-xs tracking-[0.3em] text-[#52525B] mb-4 uppercase">Join Our Team</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl italic mb-4">Open Positions</h1>
          <p className="text-[#A1A1AA] text-lg max-w-xl">
            Help us bring stories to every screen, in every language.
          </p>
        </motion.div>

        {departments.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {departments.map((d) => (
              <button key={d} data-testid={`filter-${d}`} onClick={() => setFilterDept(d)}
                className={`mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-full border transition-all ${
                  filterDept === d
                    ? "border-[#17B352] text-[#17B352] bg-[rgba(23,179,82,0.1)]"
                    : "border-[#1f1f23] text-[#52525B] hover:border-[#52525B]"
                }`}>
                {d}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16"><div className="mono-font text-sm text-[#52525B]">Loading positions...</div></div>
        ) : filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16 glass-card rounded-xl">
            <h3 className="text-2xl mb-3 italic">No open positions right now</h3>
            <p className="text-[#A1A1AA] mb-6">Check back soon or join our translator network.</p>
            <Link to="/#translator" className="glow-button inline-block">Join as Translator</Link>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {filtered.map((job) => (
              <JobCard key={job.id} job={job} isExpanded={expandedId === job.id}
                onToggle={() => setExpandedId(expandedId === job.id ? null : job.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { JobDetailPage };
export default CareersPage;
